import {faAngleDown, faPlus, faSpinnerThird, faTimes, faUser} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Dropdown, Space, Tag, Tooltip} from 'antd';
import {uniqBy} from 'lodash';
import {useMemo, useState} from 'react';

import {Locale} from '../../../../provider/locale/locale';
import {useSnackbar} from '../../../../provider/snackbar/snackbar-hook';
import {fetchReviewsReplyTagsSend} from '../../../../service/reviews/reviews';
import {useResponsibleUserSelect} from '../../../../service/reviews/reviews-responsible-users';
import {fetchAspectSentimentUpdate, TagDataType, useReviewSentiments} from '../../../../service/reviews/reviews-tags';
import {
    ReviewDataTagType,
    ReviewDataType,
    ReviewResponsibleUserType,
    ReviewSentimentEnum,
    ReviewSentimentOptionType,
    ReviewSentimentType,
} from '../../../../service/reviews/reviews-type';
import {classNames} from '../../../../util/css';
import {ApiError, getErrorMessages} from '../../../../util/error';
import {useReviewStateHook} from '../reviews-state/reviews-state-provider';
import {TagsPopup} from '../tags-popup/tags-popup';

import * as styles from './tag-list.scss';

type PropsType = {
    refreshReview?: (reviewId: number) => Promise<unknown>;
    reviewId: number;
    deletedAt: string | null;
    tags: Array<ReviewDataTagType> | null;
    responsibleUser?: ReviewResponsibleUserType | null;
    updateReview: (reviewId: number, updateData: Partial<ReviewDataType>) => void;
};

// eslint-disable-next-line max-statements
export function TagList(props: PropsType): JSX.Element | null {
    const {reviewId, tags, deletedAt, responsibleUser, updateReview, refreshReview} = props;
    const tagList = tags || [];
    const {isShowAutotags} = useReviewStateHook();
    const {snackbar} = useSnackbar();
    const [isTagsPopupOpen, setIsTagsPopupOpen] = useState<boolean>(false);
    const [isCreating, setIsCreating] = useState<boolean>(false);
    const [removingTagIds, setRemovingTagIds] = useState<Array<number>>([]);
    const [isResponsibleUserLoading, setIsResponsibleUserLoading] = useState<boolean>(false);
    const [hoveredTagId, setHoveredTagId] = useState<number | null>(null);
    const [openedSentimentTagId, setOpenedSentimentTagId] = useState<number | null>(null);
    const [updatingSentimentTagId, setUpdatingSentimentTagId] = useState<number | null>(null);

    const {mutateAsync} = useResponsibleUserSelect();
    const {data: sentimentsMap} = useReviewSentiments();

    const sentimentItems = useMemo<Array<ReviewSentimentOptionType>>(
        () =>
            sentimentsMap
                ? [
                      {
                          key: ReviewSentimentEnum.positive,
                          label: sentimentsMap.positive,
                      },
                      {
                          key: ReviewSentimentEnum.neutral,
                          label: sentimentsMap.neutral,
                      },
                      {
                          key: ReviewSentimentEnum.negative,
                          label: sentimentsMap.negative,
                      },
                  ]
                : [],
        [sentimentsMap]
    );

    async function sendTags(repliersTagsData: Array<ReviewDataTagType>) {
        const uniqueTagId = new Set(repliersTagsData.map((tag) => tag.id));

        const data = {
            tag_ids: [...uniqueTagId],
        };

        try {
            const response = await fetchReviewsReplyTagsSend(reviewId, data);

            if (response.status === 'OK') {
                await updateReview(reviewId, {tags: uniqBy(repliersTagsData, 'id')});

                if (refreshReview) {
                    await refreshReview(reviewId);
                }
            }

            setIsTagsPopupOpen(false);
            snackbar.success(<Locale stringKey="REVIEWS__REVIEW_ANSWER_CHANGE_TAG_SUCCESS" />);
        } catch (error: unknown) {
            snackbar.error(<Locale stringKey="REVIEWS__REVIEW_ANSWER_CHANGE_TAG_FAIL" />);

            if (!(error instanceof ApiError)) {
                throw error;
            }
        }
    }

    async function handleRemoveTag(id: number) {
        const newRemovingTagIds = [...removingTagIds, id];
        const newTagList = tagList.filter((tag) => !newRemovingTagIds.includes(tag.id));

        setRemovingTagIds(newRemovingTagIds);
        await sendTags([...new Set(newTagList)]);
        setRemovingTagIds((previousRemovingTagIds) => previousRemovingTagIds.filter((innerId) => innerId !== id));
    }

    async function handleApplyTags(newSelectedTagList: Array<TagDataType>) {
        const newTagIdsList = newSelectedTagList.map((tag) => {
            return {id: tag.id, isAspect: false, title: tag.title, folderId: tag.folder?.id || null};
        });

        const autoTags = tagList.filter((tag) => tag.isAspect);

        setIsCreating(true);
        await sendTags([...autoTags, ...newTagIdsList]);
        setIsCreating(false);
    }

    function onAddTagClick() {
        setIsTagsPopupOpen(true);
    }

    async function handleCloseResponsibleUserTag() {
        setIsResponsibleUserLoading(true);

        await mutateAsync(
            {userId: null, reviewId},
            {
                onSuccess: () => {
                    updateReview(reviewId, {responsibleUser: null});

                    if (refreshReview) {
                        refreshReview(reviewId);
                    }
                },
            }
        );

        setIsResponsibleUserLoading(false);
    }

    async function handleChangeAspectSentiment(tagId: number, sentiment: ReviewSentimentType) {
        if (!sentimentsMap) {
            return;
        }

        if (updatingSentimentTagId === tagId) {
            return;
        }

        setUpdatingSentimentTagId(tagId);
        setOpenedSentimentTagId(null);

        try {
            await fetchAspectSentimentUpdate(tagId, {review_id: reviewId, sentiment});

            const newTags = tagList.map((tag) =>
                tag.id === tagId
                    ? {
                          ...tag,
                          sentiment: sentiment as ReviewSentimentEnum,
                      }
                    : tag
            );

            updateReview(reviewId, {tags: newTags});

            if (refreshReview) {
                await refreshReview(reviewId);
            }
        } catch (error: unknown) {
            if (error instanceof ApiError && error.statusCode === 409) {
                const message = getErrorMessages(error.jsonData)[0] || error.message;

                snackbar.error(message);
                return;
            }

            throw error;
        } finally {
            setUpdatingSentimentTagId(null);
            setOpenedSentimentTagId(null);
        }
    }

    return (
        <ul className={classNames(styles.TagList)}>
            {responsibleUser && (
                <li className={styles.TagList_item}>
                    <Tag
                        className={classNames(
                            styles.TagList_itemTag,
                            styles.TagList_itemAutotag,
                            styles.TagList_responsibleUser
                        )}
                        closable={!isResponsibleUserLoading}
                        onClose={handleCloseResponsibleUserTag}
                    >
                        <FontAwesomeIcon icon={faUser} />
                        &nbsp;
                        <Locale stringKey="REVIEWS__RESPONSIBLE_USER__PLACEHOLDER" />
                        :&nbsp;{responsibleUser.fullName}
                        {isResponsibleUserLoading && (
                            <>
                                {' '}
                                <FontAwesomeIcon icon={faSpinnerThird} spin />
                            </>
                        )}
                    </Tag>
                </li>
            )}
            {tagList.map(
                // eslint-disable-next-line complexity, sonarjs/cognitive-complexity
                (tagData: ReviewDataTagType): JSX.Element | null => {
                    const {id, title, isAspect, sentiment} = tagData;
                    const isTagRemoving = removingTagIds.includes(id);
                    const isAspectTagVisible = !isAspect || isShowAutotags;

                    if (!isAspectTagVisible) {
                        return null;
                    }

                    const isHovered = hoveredTagId === id;
                    const isSentimentDropdownOpen = openedSentimentTagId === id;
                    const isSentimentUpdating = updatingSentimentTagId === id;

                    const tagClassNames = classNames(styles.TagList_itemTag, {
                        [styles.TagList_itemAutotag]: isAspect,
                        [styles.TagList_aspectNegative]: isAspect && sentiment === ReviewSentimentEnum.negative,
                        [styles.TagList_aspectNeutral]: isAspect && sentiment === ReviewSentimentEnum.neutral,
                        [styles.TagList_aspectPositive]: isAspect && sentiment === ReviewSentimentEnum.positive,
                        [styles.TagList_disabled]: Boolean(deletedAt),
                    });

                    const aspectActionIcon = isSentimentUpdating ? faSpinnerThird : isHovered ? faTimes : faAngleDown;

                    const content = (
                        <Tag
                            className={tagClassNames}
                            key={id}
                            onMouseEnter={() => setHoveredTagId(id)}
                            onMouseLeave={() =>
                                setHoveredTagId((previousId) => (previousId === id ? null : previousId))
                            }
                        >
                            <Space size={4}>
                                <span>{title}</span>
                                {isAspect && !isTagRemoving && (
                                    <button
                                        className={styles.TagList_aspectActionButton}
                                        onClick={(event) => {
                                            event.stopPropagation();

                                            if (isHovered) {
                                                handleRemoveTag(id);
                                            }
                                        }}
                                        type="button"
                                    >
                                        <FontAwesomeIcon icon={aspectActionIcon} spin={isSentimentUpdating} />
                                    </button>
                                )}
                            </Space>
                            {isTagRemoving && (
                                <>
                                    {' '}
                                    <FontAwesomeIcon icon={faSpinnerThird} spin />
                                </>
                            )}
                        </Tag>
                    );

                    return (
                        <li className={styles.TagList_item} key={id}>
                            <Tooltip title={isAspect ? <Locale stringKey="REVIEW__AUTO_TAG__TITLE" /> : ''}>
                                {isAspect && sentimentItems.length > 0 ? (
                                    <Dropdown
                                        menu={{
                                            items: sentimentItems,
                                            onClick: ({key}) =>
                                                handleChangeAspectSentiment(id, key as ReviewSentimentType),
                                        }}
                                        onOpenChange={(open) => {
                                            setOpenedSentimentTagId(open ? id : null);
                                        }}
                                        open={isSentimentDropdownOpen}
                                        placement="bottomLeft"
                                        trigger={['click']}
                                    >
                                        {content}
                                    </Dropdown>
                                ) : (
                                    <Tag
                                        className={tagClassNames}
                                        closable={!isTagRemoving && !isAspect}
                                        key={id}
                                        onClose={() => handleRemoveTag(id)}
                                    >
                                        {title}
                                        {isTagRemoving && (
                                            <>
                                                {' '}
                                                <FontAwesomeIcon icon={faSpinnerThird} spin />
                                            </>
                                        )}
                                    </Tag>
                                )}
                            </Tooltip>
                        </li>
                    );
                }
            )}

            <li className={styles.TagList_item} key="new-tag-button">
                <Tag className={classNames(styles.TagList_addTagButton)} onClick={onAddTagClick}>
                    <Space size={4}>
                        <FontAwesomeIcon icon={faPlus} />
                        <Locale stringKey="REVIEWS__REVIEW__ADD_TAG" />
                    </Space>
                </Tag>

                <TagsPopup
                    currentTags={tagList}
                    isCreating={isCreating}
                    isOpen={isTagsPopupOpen}
                    onApply={handleApplyTags}
                    onClose={() => setIsTagsPopupOpen(false)}
                />
            </li>
        </ul>
    );
}
